/**
 * Shopify-Katalog-Sync Dialog (PIM P2).
 * Zwei Stufen: 1) rein lesende Analyse → Report, 2) Übernahme nach Bestätigung.
 * Operationsdaten (B2B-Preis, Bestand, Lagerort, Kundenpreise) bleiben unberührt.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { analyzeCatalog, applyMirror, applyImport } from '@/lib/shopifyCatalogSync';

export default function ShopifySyncDialog({ open, onOpenChange }) {
  const [phase, setPhase] = useState('idle'); // idle | analyzing | report | applying | done
  const [progress, setProgress] = useState('');
  const [report, setReport] = useState(null);
  const [selectedNew, setSelectedNew] = useState(new Set());
  const [result, setResult] = useState(null);
  const queryClient = useQueryClient();

  const reset = () => { setPhase('idle'); setReport(null); setResult(null); setProgress(''); };

  const runAnalysis = async () => {
    setPhase('analyzing');
    try {
      const r = await analyzeCatalog((n) => setProgress(`${n} Shopify-Produkte geladen…`));
      setReport(r);
      // Produkte mit Dubletten-Verdacht bewusst NICHT vorauswählen —
      // sonst entsteht ein zweiter Datensatz und die Kundenpreise bleiben am alten hängen.
      setSelectedNew(new Set(r.newInShopify.map((_, i) => i).filter((i) => !r.newInShopify[i].possibleDuplicate)));
      setPhase('report');
    } catch (err) {
      toast.error('Analyse fehlgeschlagen: ' + err.message);
      setPhase('idle');
    }
  };

  const runApply = async () => {
    setPhase('applying');
    const errors = [];
    try {
      setProgress('Gematchte Produkte werden gespiegelt…');
      const warnings = [];
      const m = await applyMirror(report.matched, (d, t) => setProgress(`Spiegeln ${d}/${t}…`));
      errors.push(...m.errors);
      warnings.push(...(m.warnings || []));
      const toImport = report.newInShopify.filter((_, i) => selectedNew.has(i));
      if (toImport.length > 0) {
        const im = await applyImport(toImport, (d, t) => setProgress(`Import ${d}/${t}…`));
        errors.push(...im.errors);
        warnings.push(...(im.warnings || []));
      }
      setResult({ mirrored: report.matched.length, imported: toImport.length, errors, warnings });
      setPhase('done');
      queryClient.invalidateQueries(['admin-products']);
      queryClient.invalidateQueries(['products']);
      queryClient.invalidateQueries(['product-variants-all']);
      toast.success('Shopify-Sync abgeschlossen');
    } catch (err) {
      toast.error('Sync fehlgeschlagen: ' + err.message);
      setPhase('report');
    }
  };

  const toggleNew = (i) => {
    setSelectedNew((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shopify-Katalog-Sync</DialogTitle>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Schritt 1 ist eine <b>rein lesende Analyse</b> — es wird nichts verändert.
              Übernommen wird erst nach Ihrer Bestätigung. B2B-Preise, Bestände,
              Lagerorte und Kundenpreise werden <b>nie</b> angefasst.
            </p>
            <Button onClick={runAnalysis} className="w-full min-h-[44px] bg-brand-950 hover:bg-brand-900">
              Analyse starten
            </Button>
          </div>
        )}

        {(phase === 'analyzing' || phase === 'applying') && (
          <div className="py-8 text-center text-sm text-slate-500">
            <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin text-slate-400" />
            {progress || 'Bitte warten…'}
          </div>
        )}

        {phase === 'report' && report && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-2xl font-display font-extrabold">{report.matched.length}</p>
                <p className="text-xs text-slate-500">per SKU gematcht → werden gespiegelt</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-2xl font-display font-extrabold">{report.fcOnlyCount}</p>
                <p className="text-xs text-slate-500">nur in FieldConnect → unberührt</p>
              </div>
            </div>

            {report.skuless.length > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-xs">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {report.skuless.length} Shopify-Produkte ohne SKU können nicht gematcht werden:{' '}
                  {report.skuless.slice(0, 5).map((s) => s.shopify.title).join(', ')}
                  {report.skuless.length > 5 && ' …'} — bitte in Shopify SKUs vergeben.
                </span>
              </div>
            )}

            {report.skuConflicts?.length > 0 && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-xs">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <b>{report.skuConflicts.length} SKU-Konflikte — werden NICHT importiert:</b>
                  <ul className="list-disc ml-4 mt-1 space-y-0.5">
                    {report.skuConflicts.slice(0, 8).map((c) => (
                      <li key={c.shopify.id}><span className="font-mono">{c.sku}</span> · {c.shopify.title} — {c.reason}</li>
                    ))}
                    {report.skuConflicts.length > 8 && <li>…</li>}
                  </ul>
                  <p className="mt-1">Bitte in Shopify eindeutige SKUs vergeben, dann Sync wiederholen.</p>
                </div>
              </div>
            )}

            {report.newInShopify.some((n) => n.possibleDuplicate) && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-xs">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <b>
                    {report.newInShopify.filter((n) => n.possibleDuplicate).length} mögliche Dubletten —
                    nicht vorausgewählt:
                  </b>
                  <ul className="list-disc ml-4 mt-1 space-y-0.5">
                    {report.newInShopify
                      .filter((n) => n.possibleDuplicate)
                      .slice(0, 8)
                      .map((n) => (
                        <li key={n.shopify.id}>
                          {n.shopify.title} ≈ „{n.possibleDuplicate.name}"
                          {n.possibleDuplicate.is_active ? '' : ' (inaktiv)'}
                        </li>
                      ))}
                  </ul>
                  <p className="mt-1">
                    Beim Import entsteht ein <b>zweiter</b> Datensatz — Kundenpreise, Belege und
                    Lagerbewegungen bleiben am alten hängen. Besser: dem Altprodukt in Shopify
                    dieselbe SKU geben und erneut synchronisieren.
                  </p>
                </div>
              </div>
            )}

            {report.newInShopify.length > 0 && (
              <div>
                <p className="font-semibold mb-2">
                  Neu aus Shopify übernehmen ({selectedNew.size}/{report.newInShopify.length} gewählt):
                </p>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {report.newInShopify.map((item, i) => (
                    <label key={item.shopify.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={selectedNew.has(i)} onCheckedChange={() => toggleNew(i)} />
                      <span className="flex-1 truncate">
                        {item.shopify.title}
                        {item.possibleDuplicate && (
                          <span className="ml-2 text-[11px] text-amber-700">
                            ≈ {item.possibleDuplicate.name}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        {item.shopify.variants.nodes[0]?.sku || '—'}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Importierte Produkte starten mit Shopify-Bestand; B2B-Preis bleibt leer (Ihre Entscheidung).
                </p>
              </div>
            )}

            <Button onClick={runApply} className="w-full min-h-[44px] bg-brand-950 hover:bg-brand-900">
              Übernehmen ({report.matched.length} spiegeln
              {selectedNew.size > 0 ? ` + ${selectedNew.size} importieren` : ''})
            </Button>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              {result.mirrored} gespiegelt · {result.imported} importiert
            </div>
            {result.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-xs max-h-40 overflow-y-auto">
                <b>{result.warnings.length} Hinweis(e) — doppelte SKUs in Shopify:</b>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
                <p className="mt-1">
                  Inhalt und Preis wurden trotzdem übernommen. Bitte in Shopify eindeutige
                  SKUs vergeben und erneut synchronisieren.
                </p>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs max-h-40 overflow-y-auto">
                <b>{result.errors.length} Fehler:</b>
                <ul className="list-disc ml-4 mt-1">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <Button variant="outline" className="w-full min-h-[44px]" onClick={() => { reset(); onOpenChange(false); }}>
              Schließen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
